/**
 * 탭 갱신/가시성/언어/생명주기 라우팅 — 등록제 레지스트리 (Phaser 비의존, 순수 node 테스트 가능).
 *
 * apps/module-brick-idle `ui/TabRefresher`에서 패키지로 이동 (2026-06-12) — 앱에는 re-export shim 유지.
 * 도메인 부트스트래퍼가 자기 탭 핸들을 register()하면 scene의 refreshAll/refreshActiveTab/
 * setActiveTab/setLanguage switch·나열에서 그 탭이 빠진다 (미등록 탭은 레거시 경로 유지).
 *
 * TabLifecycle:
 *  - registerLazy(): 첫 활성화 시 mount() 1회(미방문 탭은 아예 미생성 — 숨은 탭 stale/겹침 원천 제거),
 *    이후 show/hide는 setVisible 토글(상태 보존). resident=true는 등록 즉시 mount(상주 탭).
 *  - destroyOnHide=true는 hide 시 dispose+언마운트(다음 표시 때 재mount) — 메모리 민감 탭 opt-in.
 *  - disposeAll(): scene SHUTDOWN에서 mount 역순 일괄 해제.
 *
 * **성능 — signature 기반 refresh diff (⚠ 패키지 레벨 자동 방어):**
 * 핸들이 `signature()`(가벼운 데이터 시그니처)를 제공하면, refresh/refreshAll이 직전 시그니처와 비교해
 * **동일하면 `update()`를 스킵**한다. update가 데이터 빌더(buildXxxData) + 뷰 갱신을 캡슐화하므로, 스킵 시
 * 무거운 빌더(스탯 maxAffordable 누진루프·도감 80카드 setText) 자체가 실행되지 않는다. GameScene이
 * 250ms 타이머·mutation flush로 활성 탭을 매번 refresh해도, 데이터가 안 바뀌면 비용이 0이 된다.
 * signature 미제공 핸들은 기존대로 매번 update(안전 fallback) — 다른 agent가 모르고 탭을 추가해도 동작 정상.
 * 시그니처 무효화: 언어/방향 전환(셀 본문이 update의 t() 보간에서 나옴)·재mount·재register 시 캐시를 비운다.
 */

/** 레지스트리 계약 — zero-arg update. 보통 boot 어댑터가 데이터 빌더를 바인딩해 구현한다. */
export interface TabHandle {
  /** 표시 데이터 재구성 — 빌더 호출 + view.update (Humble View 규약). */
  update(): void;
  /**
   * 표시 데이터의 가벼운 시그니처 — refresh가 직전과 동일하면 update를 스킵(빌더 미실행). opt-in.
   * **buildXxxData보다 훨씬 가벼운 입력**(레벨/골드/발견상태 concat)만 써야 의미가 있다. 미제공 시 매번 update.
   */
  signature?(): string;
  setVisible(visible: boolean): void;
  /** 정적 라벨 인플레이스 재번역 (언어 전환). 없으면 생략 가능. */
  applyLanguage?(): void;
  /** RTL↔LTR 방향 전환 시 좌표 미러 재배치. 미지원 탭(LTR 고정)은 생략. */
  relayout?(dir: "ltr" | "rtl"): void;
  /**
   * 콘텐츠 영역(rect) 변경 시 재배치 — 게임뷰 펼치기/접기로 탭 콘텐츠 패널이 커지거나 줄 때 호출.
   * 셸이 새 콘텐츠 rect를 브로드캐스트하면 각 탭이 그 안으로 재배치한다(미구현 탭은 커진 패널만 차지). opt-in.
   */
  resize?(rect: TabContentRect): void;
  /** 탭 해제 — 뷰 destroy/타이머 정리 (TabLifecycle). lazy 탭은 구현 권장. */
  dispose?(): void;
}

/** 탭 콘텐츠 영역 사각형 — 셸이 펼치기/접기 시 각 탭에 브로드캐스트(패널 9-slice 안쪽 inset). */
export interface TabContentRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * 탭 뷰 클래스용 DIP 계약 — 데이터 주입형 `update(data)` (Humble View: 데이터는 호출자가 조립).
 *
 * `TabHandle`(zero-arg)과의 관계: 뷰 클래스가 본 계약을 구현하고, boot 어댑터가
 * `{ update: () => view.update(buildData()), ... }`로 빌더를 바인딩해 TabHandle을 만든다.
 * 컨테이너/상주 뷰처럼 외부 경로로 갱신되는 뷰는 update 생략 가능 — 공통 분모는 setVisible.
 */
export interface TabViewContract<TData = void> {
  /** 표시 데이터 갱신 — 컨테이너/상주 뷰(외부 갱신 경로)는 생략 가능. */
  update?(data: TData): void;
  setVisible(visible: boolean): void;
  applyLanguage?(): void;
  relayout?(dir: "ltr" | "rtl"): void;
  /** 콘텐츠 영역 변경(게임뷰 펼치기/접기) 시 재배치. opt-in. */
  resize?(rect: TabContentRect): void;
  dispose?(): void;
}

/** TabLifecycle 등록 — boot이 mount 팩토리와 생명주기 정책을 선언. */
export interface TabMountRegistration {
  /** 탭 뷰 생성 + 핸들 반환 — 첫 활성화(또는 resident면 등록 즉시) 시 1회 호출. */
  mount(): TabHandle;
  /** true면 등록 즉시 mount — 상주 탭 (예: 항상 갱신·전역 입력 설정 의존 탭). */
  resident?: boolean;
  /** true면 hide 시 dispose+언마운트(재표시 때 재mount) — 메모리 민감 탭 opt-in. */
  destroyOnHide?: boolean;
}

/**
 * TabViewContract 구현 뷰 → TabHandle 어댑터. boot은 `update`(데이터 빌더 바인딩)만 넘기고
 * setVisible/applyLanguage/relayout/dispose는 뷰에서 자동 위임한다(뷰에 메서드가 있을 때만 핸들에 노출).
 *
 * boot이 핸들 콜백을 손으로 나열하다 일부(특히 relayout)를 빠뜨리는 버그 클래스를 구조적으로 차단한다
 * — 2026-06-13 dungeon/codex/compose가 register 핸들에 relayout 연결을 누락해 RTL 전환 시
 * 그 탭만 정렬/미러 미적용된 버그가 계기. 헬퍼를 쓰면 뷰의 relayout이 자동으로 핸들에 연결되어 누락 불가.
 *
 * @param signature (opt-in) 표시 데이터의 가벼운 시그니처 — refresh diff용. 동일하면 update 스킵(빌더 미실행).
 */
export function viewTabHandle<TData = void>(
  view: TabViewContract<TData>,
  update: () => void,
  signature?: () => string,
): TabHandle {
  const handle: TabHandle = {
    update,
    setVisible: (visible) => view.setVisible(visible),
  };
  if (signature) handle.signature = signature;
  if (view.applyLanguage) handle.applyLanguage = () => view.applyLanguage!();
  if (view.relayout) handle.relayout = (dir) => view.relayout!(dir);
  if (view.resize) handle.resize = (rect) => view.resize!(rect);
  if (view.dispose) handle.dispose = () => view.dispose!();
  return handle;
}

export class TabRegistry {
  private readonly handles = new Map<string, TabHandle>();
  private readonly mounts = new Map<string, TabMountRegistration>();
  /** 탭별 직전 update 시그니처 — 동일하면 refresh가 update를 스킵(signature 제공 핸들만). */
  private readonly lastSig = new Map<string, string>();

  /** 이미 생성된 핸들을 즉시 등록 (lazy mount 불필요한 상주 뷰용). 같은 tabId 재등록 시 덮어쓴다. */
  register(tabId: string, handle: TabHandle): void {
    this.handles.set(tabId, handle);
    this.lastSig.delete(tabId); // 새 핸들 — 다음 refresh 강제 update(stale 캐시 무효화)
  }

  /** lazy mount 등록 (TabLifecycle) — resident면 즉시 mount, 아니면 첫 applyVisibility(true)에서. */
  registerLazy(tabId: string, registration: TabMountRegistration): void {
    this.mounts.set(tabId, registration);
    if (registration.resident) this.ensureMounted(tabId);
  }

  /** 등록 여부 — mount 전 lazy 탭도 본 레지스트리 소유(레거시 switch 진입 차단). */
  has(tabId: string): boolean {
    return this.handles.has(tabId) || this.mounts.has(tabId);
  }

  /**
   * 등록 탭이면 갱신 후 true — 호출자(레거시 switch)는 true일 때 자기 분기를 건너뛴다.
   * lazy 미mount 탭은 갱신할 뷰가 없으므로 no-op true (표시 시 mount→update 경로가 채움).
   * signature 제공 핸들은 직전 시그니처와 동일하면 update 스킵(빌더 미실행) — 무거운 탭 매 refresh 렉 방지.
   */
  refresh(tabId: string): boolean {
    const h = this.handles.get(tabId);
    if (h) {
      this.runUpdate(tabId, h);
      return true;
    }
    return this.mounts.has(tabId);
  }

  /** mount된 모든 탭 갱신 (refreshAll 경로) — 미mount lazy 탭은 뷰가 없어 대상 아님. signature diff 게이트 적용. */
  refreshAll(): void {
    for (const [tabId, h] of this.handles) this.runUpdate(tabId, h);
  }

  /** signature가 직전과 동일하면 update 스킵, 아니면 캐시 갱신 후 update. signature 미제공이면 항상 update. */
  private runUpdate(tabId: string, h: TabHandle): void {
    if (h.signature) {
      const sig = h.signature();
      if (sig === this.lastSig.get(tabId)) return;
      this.lastSig.set(tabId, sig);
    }
    h.update();
  }

  /**
   * 등록 탭들의 가시성을 visibility 맵으로 일괄 확정 — 미포함 키는 false (겹침 방어).
   * lazy 탭은 표시 직전 mount(첫 1회), destroyOnHide 탭은 hide 시 dispose+언마운트.
   */
  applyVisibility(visibility: Readonly<Record<string, boolean | undefined>>): void {
    for (const [tabId, visible] of Object.entries(visibility)) {
      if (visible) this.ensureMounted(tabId);
    }
    for (const [tabId, h] of [...this.handles]) {
      const visible = visibility[tabId] ?? false;
      h.setVisible(visible);
      if (!visible && this.mounts.get(tabId)?.destroyOnHide) {
        h.dispose?.();
        this.handles.delete(tabId); // 다음 표시 때 재mount
        this.lastSig.delete(tabId); // 재mount 시 빈 화면 — 강제 update 위해 캐시 무효화
      }
    }
  }

  /** mount된 탭들의 정적 라벨 재번역 (언어 전환 경로) — 미mount 탭은 mount 시점 언어로 생성됨. */
  applyLanguageAll(): void {
    // 셀 본문(cellName/breakdown/result)이 update의 t() 보간에서 나오는 탭이 있어, 언어 전환 후
    // 다음 refresh가 강제 update되도록 시그니처 캐시를 비운다(아니면 옛 언어 본문 stale).
    this.lastSig.clear();
    for (const h of this.handles.values()) h.applyLanguage?.();
  }

  /** mount된 탭들의 RTL/LTR 좌표 재배치 (방향 전환 경로) — relayout 미구현 탭은 건너뜀. */
  relayoutAll(dir: "ltr" | "rtl"): void {
    this.lastSig.clear(); // 방향 전환 — 다음 refresh 강제 update(좌표 재배치 반영)
    for (const h of this.handles.values()) h.relayout?.(dir);
  }

  /**
   * 특정 탭에 새 콘텐츠 rect 브로드캐스트 (게임뷰 펼치기/접기) — mount된 핸들만, resize 미구현 탭은 no-op.
   * 시그니처 캐시도 무효화(재배치 후 다음 refresh가 새 좌표로 강제 update되게). 미mount 탭은 표시 시점에 셸이 재호출.
   */
  resize(tabId: string, rect: TabContentRect): void {
    const h = this.handles.get(tabId);
    if (!h?.resize) return;
    this.lastSig.delete(tabId);
    h.resize(rect);
  }

  /** scene SHUTDOWN — mount 역순 일괄 dispose + 레지스트리 초기화 (restart 시 boot이 재등록). */
  disposeAll(): void {
    for (const h of [...this.handles.values()].reverse()) h.dispose?.();
    this.handles.clear();
    this.mounts.clear();
    this.lastSig.clear();
  }

  private ensureMounted(tabId: string): void {
    if (this.handles.has(tabId)) return;
    const reg = this.mounts.get(tabId);
    if (!reg) return;
    this.handles.set(tabId, reg.mount());
    this.lastSig.delete(tabId); // 새 핸들 — 다음 refresh 강제 update
  }
}
